import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AdminGalleryAlbum,
  AdminGalleryPhoto,
  AdminPhotoComment,
  PhotoCommentStatus,
} from "@tih/shared";
import {
  ApiError,
  adminCreateGalleryAlbum,
  adminDeleteGalleryAlbum,
  adminDeleteGalleryPhoto,
  adminDeletePhotoComment,
  adminFetchGalleryAlbums,
  adminFetchGalleryPhotos,
  adminFetchPhotoComments,
  adminModeratePhotoComment,
  adminUpdateGalleryAlbum,
} from "../../../api/client";
import { useEventHub } from "../../../context/EventHubContext";
import { useGalleryUpload } from "../../../context/GalleryUploadContext";
import { eventDisplayTitle } from "../../../lib/event-admin-utils";
import { GalleryAlbumEditor } from "./GalleryAlbumEditor";

const COMMENT_TABS: { key: PhotoCommentStatus | "ALL"; label: string }[] = [
  { key: "PENDING", label: "Pending" },
  { key: "PUBLISHED", label: "Published" },
  { key: "REJECTED", label: "Rejected" },
  { key: "ALL", label: "All" },
];

export function EventGalleryPanel() {
  const hub = useEventHub();
  const { onAlbumUploadComplete, startDownloadZip, jobs } = useGalleryUpload();
  const [album, setAlbum] = useState<AdminGalleryAlbum | null>(null);
  const [photos, setPhotos] = useState<AdminGalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentTab, setCommentTab] = useState<PhotoCommentStatus | "ALL">("PENDING");
  const [comments, setComments] = useState<AdminPhotoComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const albumIdRef = useRef<number | null>(null);
  albumIdRef.current = album?.id ?? null;

  const loadAlbum = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const albums = await adminFetchGalleryAlbums();
      const match = albums.find((a) => a.eventId === hub.eventId) ?? null;
      setAlbum(match);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load album");
      setAlbum(null);
    } finally {
      setLoading(false);
    }
  }, [hub.eventId]);

  const loadPhotos = useCallback(async (albumId: number) => {
    setPhotosLoading(true);
    try {
      setPhotos(await adminFetchGalleryPhotos(albumId));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setPhotosLoading(false);
    }
  }, []);

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      setComments(await adminFetchPhotoComments(commentTab));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setCommentsLoading(false);
    }
  }, [commentTab]);

  useEffect(() => {
    void loadAlbum();
  }, [loadAlbum]);

  useEffect(() => {
    if (album) void loadPhotos(album.id);
    else setPhotos([]);
  }, [album?.id, loadPhotos]);

  useEffect(() => {
    if (commentsOpen) void loadComments();
  }, [commentsOpen, loadComments]);

  useEffect(() => {
    return onAlbumUploadComplete((albumId) => {
      void loadAlbum();
      if (albumIdRef.current === albumId) void loadPhotos(albumId);
      void hub.refreshSummary();
    });
  }, [onAlbumUploadComplete, loadAlbum, loadPhotos, hub]);

  async function createAlbum() {
    if (!hub.event) return;
    setCreating(true);
    setError(null);
    try {
      const title = eventDisplayTitle(hub.event);
      await adminCreateGalleryAlbum({ title, eventId: hub.eventId });
      setSuccess("Photo album created.");
      await loadAlbum();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  async function updateAlbum(id: number, patch: Parameters<typeof adminUpdateGalleryAlbum>[1]) {
    setError(null);
    try {
      await adminUpdateGalleryAlbum(id, patch);
      setSuccess("Album updated.");
      await loadAlbum();
      await hub.refreshSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function removeAlbum(id: number) {
    if (!confirm("Delete this album and all its photos?")) return;
    await adminDeleteGalleryAlbum(id);
    await loadAlbum();
    await hub.refreshSummary();
  }

  async function removePhoto(albumId: number, photoId: number) {
    if (!confirm("Remove this photo?")) return;
    await adminDeleteGalleryPhoto(photoId);
    await loadPhotos(albumId);
    await loadAlbum();
    await hub.refreshSummary();
  }

  async function moderateComment(id: number, status: "PUBLISHED" | "REJECTED") {
    await adminModeratePhotoComment(id, status);
    await loadComments();
  }

  async function removeComment(id: number) {
    if (!confirm("Delete this comment?")) return;
    await adminDeletePhotoComment(id);
    await loadComments();
  }

  const albumComments = album
    ? comments.filter((c) => c.albumTitle === album.title)
    : comments;

  if (loading) return <div style={{ opacity: 0.5 }}>Loading album…</div>;

  return (
    <div>
      {error && <div className="form-error admin-banner">{error}</div>}
      {success && (
        <div className="form-success admin-banner" onAnimationEnd={() => setSuccess(null)}>
          {success}
        </div>
      )}

      {!album ? (
        <section className="admin-card">
          <h2 className="admin-card-title">Photo album</h2>
          <p className="admin-muted">
            Create an album to upload watermarked photos from this worship night. Published albums
            appear on <code>/gallery</code>.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={creating}
            onClick={() => void createAlbum()}
          >
            {creating ? "Creating…" : "Create photo album"}
          </button>
        </section>
      ) : (
        <section className="admin-card">
          <h2 className="admin-card-title">Photo album</h2>
          <GalleryAlbumEditor
            album={album}
            photos={photos}
            photosLoading={photosLoading}
            zipDownloadActive={jobs.some(
              (j) =>
                j.kind === "download" &&
                j.albumId === album.id &&
                j.phase === "preparing",
            )}
            onUpdate={(patch) => void updateAlbum(album.id, patch)}
            onDelete={() => void removeAlbum(album.id)}
            onDownloadZip={() => startDownloadZip(album)}
            onError={(msg) => setError(msg)}
            onRemovePhoto={(photoId) => void removePhoto(album.id, photoId)}
          />
        </section>
      )}

      {album && (
        <section className="admin-card" style={{ marginTop: 24 }}>
          <div className="admin-page-header-row" style={{ marginBottom: 12 }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>
              Photo comments
            </h3>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setCommentsOpen((v) => !v)}
            >
              {commentsOpen ? "Hide" : "Moderate comments"}
            </button>
          </div>
          {commentsOpen && (
            <>
              <div className="admin-tab-row" style={{ marginBottom: 16 }}>
                {COMMENT_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`admin-tab${commentTab === t.key ? " is-active" : ""}`}
                    onClick={() => setCommentTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {commentsLoading ? (
                <p>Loading comments…</p>
              ) : albumComments.length === 0 ? (
                <p className="admin-muted">No comments for this album in this tab.</p>
              ) : (
                <div className="admin-cards">
                  {albumComments.map((c) => (
                    <article className="admin-card" key={c.id}>
                      <div className="admin-card-meta">
                        <strong>{c.authorName}</strong>
                        {c.authorEmail && <> · {c.authorEmail}</>}
                        <br />
                        <span className="admin-badge">{c.status}</span>
                      </div>
                      {c.photoUrl && (
                        <img src={c.photoUrl} alt="" className="admin-gallery-comment-thumb" />
                      )}
                      <blockquote>{c.body}</blockquote>
                      <div className="admin-card-actions">
                        {c.status === "PENDING" && (
                          <>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => void moderateComment(c.id, "PUBLISHED")}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              onClick={() => void moderateComment(c.id, "REJECTED")}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => void removeComment(c.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
